import React, { useState, useEffect } from 'react';
import { WIDGET_TYPES, WIDGET_CATEGORIES } from './widgetTypes';
import { API_BASE } from '../../constants/api';
import { Abbrechen, Zurück } from '../../icons';

/* ── Shared hook: loads a TB image by search term + expected title suffix ── */
function useTbImage(searchTerm, titleSuffix, token) {
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    if (!token || !searchTerm) return;
    let cancelled = false;
    async function load() {
      try {
        const params = new URLSearchParams({
          textSearch: searchTerm,
          pageSize: '50',
          page: '0',
          includeSystemImages: 'true',
        });
        const res = await fetch(`${API_BASE}/images?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const images = data.data || [];
        if (!images.length || cancelled) return;

        const exactTitle = `${searchTerm} ${titleSuffix}`.toLowerCase();
        const searchLower = searchTerm.toLowerCase();
        const best =
          images.find(img => (img.title || img.name || '').toLowerCase() === exactTitle) ||
          images.find(img => (img.title || img.name || '').toLowerCase().includes(searchLower)) ||
          images[0];

        const url = best.link
          ? `${API_BASE.replace('/api', '')}${best.link}`
          : best.id?.id
            ? `${API_BASE}/image/${best.id.id}`
            : null;
        if (!url || cancelled) return;

        const imgRes = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!imgRes.ok || cancelled) return;
        const blob = await imgRes.blob();
        if (!cancelled) setImageUrl(URL.createObjectURL(blob));
      } catch {
        /* image simply stays absent */
      }
    }
    load();
    return () => {
      cancelled = true;
      setImageUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [searchTerm, titleSuffix, token]);

  return imageUrl;
}

/* ── Widget tile: uses "system widget image" suffix ── */
function WidgetTile({ widgetKey, def, onSelect, token }) {
  const imageUrl = useTbImage(def.imageSearch, 'system widget image', token);

  return (
    <button
      className="add-widget-option"
      onClick={() => onSelect(widgetKey)}
      title={def.label}
    >
      {imageUrl && (
        <div className="add-widget-option-preview">
          <img src={imageUrl} alt={def.label} className="add-widget-option-img" />
        </div>
      )}
      <span className="add-widget-option-label">{def.label}</span>
    </button>
  );
}

const CATEGORY_IMAGE_SEARCH = {
  charts: 'Charts',
  cards: 'Cards',
  tables: 'Tables',
};


function CategoryTile({ cat, onSelect, token }) {
  const searchTerm = CATEGORY_IMAGE_SEARCH[cat.key] || cat.label;
  const imageUrl = useTbImage(searchTerm, 'system bundle image', token);

  return (
    <button
      className="add-widget-option"
      onClick={() => onSelect(cat.key)}
      title={cat.label}
    >
      {imageUrl && (
        <div className="add-widget-option-preview">
          <img src={imageUrl} alt={cat.label} className="add-widget-option-img" />
        </div>
      )}
      <span className="add-widget-option-label">{cat.label}</span>
    </button>
  );
}

function AddWidgetDialog({ onSelect, onClose, token }) {
  const [selectedCategory, setSelectedCategory] = useState(null);

  const activeCategory = selectedCategory
    ? WIDGET_CATEGORIES.find(c => c.key === selectedCategory)
    : null;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="add-widget-title">
      <div className="add-widget-dialog">

        {/* Header */}
        <div className="add-widget-header">
          {activeCategory && (
            <button
              className="back-button add-widget-back"
              onClick={() => setSelectedCategory(null)}
              aria-label="Zurück zu Kategorien"
              title="Zurück zu Kategorien"
            >
              <span aria-hidden="true"><Zurück /></span>
            </button>
          )}
          <h2 id="add-widget-title">Widget hinzufügen</h2>
        </div>

        {/* Body */}
        {!activeCategory ? (
          <div className="add-widget-grid">
            {WIDGET_CATEGORIES.map(cat => (
              <CategoryTile key={cat.key} cat={cat} onSelect={(k) => setSelectedCategory(k)} token={token} />
            ))}
          </div>
        ) : (

          <div className="add-widget-grid">
            {activeCategory.widgets.map(key => {
              const def = WIDGET_TYPES[key];
              if (!def) return null;
              return (
                <WidgetTile
                  key={key}
                  widgetKey={key}
                  def={def}
                  onSelect={onSelect}
                  token={token}
                />
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="add-widget-footer">
          <button className="modal-secondary" onClick={onClose}>
            <Abbrechen /> Abbrechen
          </button>
        </div>

      </div>
    </div>
  );
}

export default AddWidgetDialog;
